import React, { useRef, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { X } from "lucide-react";
import CampSiteCard from '../CampSiteCard';
import { CampingSite, FrontendAmenities } from '@/services/campingSite/types';

interface SitePopupProps {
  site: CampingSite | null;
  onClose: () => void;
  startDate?: Date;
  endDate?: Date;
  isFullscreen?: boolean;
  containedInMap?: boolean;
  handleBookNow?: (site: CampingSite) => void;
}

const SitePopup: React.FC<SitePopupProps> = ({
  site,
  onClose,
  startDate,
  endDate,
  isFullscreen = false,
  containedInMap = false,
  handleBookNow
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
        event.stopPropagation();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const handleBookSite = (siteId: string) => {
    if (site && handleBookNow) {
      console.log(`Booking site ${siteId} via handleBookNow`);
      handleBookNow(site);
    } else {
      console.log(`Fallback booking for site ${siteId}`);
    }
  };

  if (!site) return null;

  // Convert amenities to frontend format if needed
  const siteAmenities = site.amenities as FrontendAmenities;

  if (containedInMap) {
    return (
      <Card className="shadow-xl border-2 border-tribal-primary/20 relative" ref={popupRef}>
        <div className="absolute -top-3 -right-3 z-30">
          <button 
            onClick={handleClose} 
            className="bg-tribal-primary text-white rounded-full p-2 shadow-md hover:bg-tribal-primary/80 transition-colors border-2 border-white"
            aria-label="Close popup"
          >
            <X className="h-5 w-5" strokeWidth={3} />
          </button>
        </div>
        
        {/* Bottom left close button */}
        <div className="absolute -bottom-3 -left-3 z-30">
          <button 
            onClick={handleClose} 
            className="bg-tribal-primary text-white rounded-full p-2 shadow-md hover:bg-tribal-primary/80 transition-colors border-2 border-white"
            aria-label="Close popup (bottom)"
          >
            <X className="h-5 w-5" strokeWidth={3} />
          </button>
        </div>
        
        <CardContent className="p-0">
          <CampSiteCard 
            site={{
              id: site.id,
              name: site.siteNumber,
              location: site.location === 'mountain-view' ? 'Mountain View' : 
                      site.location === 'sheep-creek' ? 'Sheep Creek' : 'Wild Horse',
              price: site.pricePerNight,
              image: `/lovable-uploads/17667c3f-5fe0-4e51-8941-dd3d0597068b.png`,
              capacity: site.maxOccupants,
              amenities: {
                petsAllowed: siteAmenities.petsAllowed,
                amperage: siteAmenities.amperage,
                wifi: siteAmenities.wifi,
                backIn: siteAmenities.backIn,
                pullThru: siteAmenities.pullThru,
                firePit: siteAmenities.firePit,
                nearLake: siteAmenities.nearLake,
                picnicTable: siteAmenities.picnicTable,
                electric50Amp: siteAmenities.electric50Amp
              },
              available: site.status === 'active',
              status: site.status,
              statusMessage: site.statusMessage
            }} 
            onBook={handleBookSite} 
            startDate={startDate} 
            endDate={endDate}
          />
        </CardContent>
      </Card>
    );
  }

  // Define position class based on fullscreen mode
  const positionClass = isFullscreen
    ? "w-[90%] max-w-md z-[9999]"
    : "w-[90%] max-w-md z-[999]";

  return (
    <div className="popup-container">
      <div 
        className="fixed inset-0 bg-black/30 z-[998]" 
        onClick={handleClose} 
      />
      
      {/* Fixed close button outside and independent of the card */}
      <button 
        onClick={handleClose} 
        className="mobile-popup-close-btn"
        aria-label="Close popup fixed"
      >
        <X className="h-7 w-7" strokeWidth={3} />
      </button>
      
      {/* Separate close button at the top for better accessibility */}
      <button 
        onClick={handleClose} 
        className="fixed z-[99999] top-4 right-4 bg-tribal-primary text-white rounded-full p-2 shadow-lg hover:bg-tribal-primary/90 transition-colors border-2 border-white"
        aria-label="Close popup (top)"
      >
        <X className="h-6 w-6" strokeWidth={3} />
      </button>
      
      <div className={`${positionClass} popup-card-wrapper`} ref={popupRef}>
        <Card className="shadow-xl border-2 border-tribal-primary/20 relative">
          {/* Top right close button */}
          <div className="absolute -top-3 -right-3 z-30">
            <button 
              onClick={handleClose} 
              className="bg-tribal-primary text-white rounded-full p-2 shadow-md hover:bg-tribal-primary/80 transition-colors border-2 border-white"
              aria-label="Close popup"
            >
              <X className="h-5 w-5" strokeWidth={3} />
            </button>
          </div>
          
          {/* New bottom left close button */}
          <div className="absolute -bottom-3 -left-3 z-30">
            <button 
              onClick={handleClose} 
              className="bg-tribal-primary text-white rounded-full p-2 shadow-md hover:bg-tribal-primary/80 transition-colors border-2 border-white"
              aria-label="Close popup (bottom)"
            >
              <X className="h-5 w-5" strokeWidth={3} />
            </button>
          </div>
          
          <CardContent className="p-0">
            <CampSiteCard 
              site={{
                id: site.id,
                name: site.siteNumber,
                location: site.location === 'mountain-view' ? 'Mountain View' : 
                        site.location === 'sheep-creek' ? 'Sheep Creek' : 'Wild Horse',
                price: site.pricePerNight,
                image: `/lovable-uploads/17667c3f-5fe0-4e51-8941-dd3d0597068b.png`,
                capacity: site.maxOccupants,
                amenities: {
                  petsAllowed: siteAmenities.petsAllowed,
                  amperage: siteAmenities.amperage,
                  wifi: siteAmenities.wifi,
                  backIn: siteAmenities.backIn,
                  pullThru: siteAmenities.pullThru,
                  firePit: siteAmenities.firePit,
                  nearLake: siteAmenities.nearLake,
                  picnicTable: siteAmenities.picnicTable,
                  electric50Amp: siteAmenities.electric50Amp
                },
                available: site.status === 'active',
                status: site.status,
                statusMessage: site.statusMessage
              }} 
              onBook={handleBookSite} 
              startDate={startDate} 
              endDate={endDate}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SitePopup;
